export const summary =
  "Full-stack engineer with two years shipping at every layer of the stack — from typed server-side compliance backends and ORM schema design to React/Redux UIs and native graphics integration. Recent focus: design-system migrations, release-engineering automation, and agentic developer workflows for VR/AR tooling.";

export const achievements = [
  {
    id: 'enforcement',
    chapter: '01 · Compliance Infrastructure',
    title: 'Designed and launched an automated policy-enforcement action for a major developer platform',
    items: [
      ['What', 'Designed and built an end-to-end automated enforcement action that replaced a manual, error-prone admin tool for restricting developers who violated platform policies.'],
      ['Scale', 'Deployed across the third-party app ecosystem of one of the largest mixed-reality platforms in the world.'],
      ['How', 'Backend on a typed server-side language with an in-house ORM — schema design, async revocation jobs, GraphQL exposure — plus the React/TypeScript frontend in the developer-management portal (banner, sidenav restrictions, redirect flow, dispute submission). Onboarded the new action onto an internal enforcement-action API platform, replacing a CRUD-based manual process.'],
      ['Result', 'Recognized in the launch announcement as POC for "driving the design and execution" of the new enforcement action. SEV1/SEV2 violations now resolve through automated workflows rather than ad-hoc manual intervention.'],
    ],
  },
  {
    id: 'mqdh-nit',
    chapter: '02 · Cross-Platform Tooling',
    title: 'Built an on-device network-impairment simulator for the Meta Quest Developer Hub',
    items: [
      ['What', 'Designed and shipped a network-impairment simulator for Meta Quest Developer Hub (MQDH), the Electron-based developer toolchain used by Meta Horizon app developers — letting them test their apps under throttled or lossy network conditions on-device.'],
      ['How', 'TypeScript + React + Redux for the UI and persisted state; parsed slider values into platform-bridge command strings; added import/export of named impairment configurations. Wired into the device-actions surface used across the toolchain.'],
      ['Result', 'Shipped end-to-end and dogfooded within the developer community.'],
    ],
  },
  {
    id: 'design-system-migration',
    chapter: '03 · Design-System Migration',
    title: 'Migrated the Meta XR Simulator UI from immediate-mode to a React-based design system',
    items: [
      ['What', "Currently leading the migration of Meta XR Simulator's developer UI from a legacy immediate-mode-GUI shell to a modern React-based component library, in service of a major product redesign."],
      ['Scope', 'Built the new application shell — toolbar/header, side navigation, multi-section input/graphics/multiplayer panels, console with detail-view drill-down, bottom tray.'],
      ['How', 'TypeScript + React with a custom internal design system; gating to safely cut over from legacy UI; co-authored a feature-parity gap analysis between the legacy and new UIs to scope remaining work.'],
    ],
  },
  {
    id: 'cli-mcp',
    chapter: '04 · Agentic Workflows',
    title: 'Built a CLI & agent integration layer for the Meta XR Simulator',
    items: [
      ['What', "Authored a Node.js-based CLI and bridge server that exposes Meta XR Simulator's frontend to agentic developer workflows — programmatic component-tree introspection, click-by-test-id, screenshot capture, etc."],
      ['How', "Process bridge between an external CLI agent and a desktop application's React tree; component traversal with both human-readable and JSON output formats; macOS-first with cross-platform extension in flight."],
      ['Why', 'Foundation for a Model Context Protocol (MCP) server integration that lets coding agents drive the simulator end-to-end — a building block for AI-assisted XR development.'],
    ],
  },
  {
    id: 'vulkan',
    chapter: '05 · Native Graphics Debugging',
    title: 'Diagnosed and fixed a Vulkan device-matching bug on Apple Silicon',
    items: [
      ['What', 'Restored a critical app-launch path on macOS Apple Silicon where a frontend and a runtime process were failing to recognize they were using the same physical GPU.'],
      ['How', 'Traced a findVulkanDeviceByID failure to MoltenVK embedding version-specific data into device/driver UUIDs across processes built with different toolchain configurations. Fixed the matching logic so the simulator’s compositor readback could initialize.'],
      ['Impact', 'Unblocked a hello-XR sample and ongoing development on Apple Silicon hardware.'],
    ],
  },
  {
    id: 'symbolication',
    chapter: '06 · Production Observability',
    title: 'Owned the symbolication pipeline for the Meta Quest Developer Hub',
    items: [
      ['What', 'Built the source-map upload pipeline so that minified JavaScript stack traces from MQDH could be symbolicated back to original source.'],
      ['How', 'Added a CI step uploading webpack source maps to a content-addressed blob store; wired symbolicated stack traces into the existing application-error logging table; rolled out to alpha, beta, and production builds.'],
    ],
  },
  {
    id: 'casting',
    chapter: '07 · Cross-Platform UI',
    title: 'Polished and shipped cross-platform UI for a wireless casting experience',
    items: [
      ['What', 'Built the Mac-side UI for a wireless casting / panel-streaming experience between a Meta Horizon headset and a desktop client.'],
      ['How', 'Series of cross-platform diffs adding base styles, typography, toggle switches, dropdowns, toolbar/header, action bar, and settings flyout — all on a shared internal React component library that targets both web and React Native Desktop runtimes.'],
    ],
  },
  {
    id: 'org-chaining',
    chapter: '08 · Backend Infrastructure',
    title: 'Built backend infrastructure for cross-org policy-violation chaining',
    items: [
      ['What', 'Built a generalized violation-event generator framework so that compliance events tied to a single application could chain to all members of the developer organization.'],
      ['How', 'Refactored the compliance-event base type to support optional application context; introduced skeleton + adapter classes to unify multiple violation generators; consolidated previously-divergent code paths.'],
    ],
  },
  {
    id: 'release-eng',
    chapter: '09 · Release Engineering · in flight',
    title: 'Automating the binary release pipeline for the Meta XR Simulator',
    items: [
      ['What', 'Currently automating the binary release pipeline for Meta XR Simulator — package merge automation, version-file generation, multi-store ship destinations, decommissioning of legacy delivery paths, and onboarding to a newer in-house release tooling stack.'],
    ],
  },
];

export const skillClusters = [
  {
    title: 'Languages',
    chips: [
      { label: 'TypeScript / JavaScript', strong: true },
      { label: 'Typed server-side (Hack-class)', strong: true },
      { label: 'Python', strong: true },
      { label: 'C++' },
      { label: 'Java' },
      { label: 'GraphQL' },
      { label: 'SQL' },
      { label: 'Bash' },
    ],
  },
  {
    title: 'Frameworks & Libraries',
    chips: [
      { label: 'React' },
      { label: 'Redux' },
      { label: 'Electron' },
      { label: 'React Native Desktop' },
      { label: 'Node.js' },
      { label: 'Webpack' },
      { label: 'Custom design systems' },
      { label: 'In-house typed ORMs' },
    ],
  },
  {
    title: 'Infrastructure & Systems',
    chips: [
      { label: 'Distributed CI orchestration' },
      { label: 'Content-addressed blob storage' },
      { label: 'Large-scale build systems (Buck-class)' },
      { label: 'Sapling / Mercurial-class VCS' },
      { label: 'Source-map & symbolication pipelines' },
      { label: 'Dynamic configuration management' },
    ],
  },
  {
    title: 'Native / Graphics / XR',
    chips: [
      { label: 'Vulkan' },
      { label: 'MoltenVK' },
      { label: 'OpenXR' },
      { label: 'macOS (Apple Silicon & Intel)' },
      { label: 'Windows desktop' },
    ],
  },
  {
    title: 'Domains',
    chips: [
      { label: 'Developer-platform policy & enforcement' },
      { label: 'Cross-platform desktop dev tooling' },
      { label: 'Spatial-computing & VR/AR tooling' },
      { label: 'Design-system migrations' },
      { label: 'Release engineering & build automation' },
      { label: 'Agentic / AI-assisted dev workflows' },
    ],
  },
];

export const timeline = [
  {
    year: '2024',
    title: 'Joined Meta as a Software Engineer',
    body:
      'Onboarded onto a developer-platform integrity team. Within ~6 months, owned the design and launch of a new automated enforcement action — taking it from spec through backend implementation, frontend wiring, safeguard updates, and launch communications. Followed with backend infrastructure work for app-level entitlement revocation and cross-org violation chaining.',
  },
  {
    year: '2025',
    title: 'Transitioned to Reality Labs developer tooling',
    body:
      'Quickly ramped on the Meta Quest Developer Hub (MQDH), shipping two major user-facing features — an on-device network-impairment simulator and a redesigned downloads / asset-management surface — plus a production source-map / symbolication pipeline. Concurrently built UI components on a wireless-casting experience between a Meta Horizon headset and a Mac client, then shifted to a creator performance-tracing tool, contributing 50+ UI changes to its live trace and snapshot views.',
  },
  {
    year: '2026 · current',
    title: "Lead engineer on the Meta XR Simulator UI redesign",
    isCurrent: true,
    body:
      "Currently driving a multi-quarter migration from a legacy immediate-mode-GUI shell to a modern React-based design system — building the new application shell from scratch, authoring the feature-parity gap analysis, and owning the release-engineering automation that ships the simulator. Concurrently building a CLI + MCP-style integration layer that lets coding agents drive the simulator end-to-end, supporting the team’s “Agentic Dev Workflows” investment.",
  },
];

export const lookingFor = [
  'Cross-functional product engineering with real users.',
  'Greenfield design + ownership opportunities, especially at the intersection of developer experience and emerging interfaces (XR, AI-assisted workflows).',
  'Teams that take design systems, build tooling, and release automation seriously as first-class engineering work.',
];
